import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Rectangle, Bar } from 'recharts'
import { useTheme, Skeleton, Stack } from '@mui/material'
import moment from 'moment'

import { DateRange } from '../components/Filters/DashboardRange'
import TooltipList from 'packages/ui/Graph/TooltipList'
import EmptyScreen from 'packages/ui/EmptyScreen'
import Legend from 'packages/ui/Graph/Legend'
import Card from 'packages/ui/Graph/Card'

const CustomCursor = props => {
  const theme = useTheme()
  const { x, y, width, height } = props;
  return <Rectangle fill={theme.palette.grey[100]} stroke={theme.palette.grey[100]} x={x} y={y} width={width} height={height} rx={6} />;
};

const CustomTooltip = ({ payload }) => {
  const { t } = useTranslation()
  const processed = payload.find(({dataKey}) => dataKey === 'count_call_success')
  const notProcessed = payload.find(({dataKey}) => dataKey === 'count_call_failure')
  return (
    <TooltipList
      title={payload[0]
        ? t('voip.datetime', {date: moment(payload[0].payload.date).format('DD/MM/yyyy'), time: moment(payload[0].payload.date).format('hh:mm')})
        : null
      }
      list={[
        {color: '#92BC62', label: t('voip.report_cdr.processed_call'), value: processed?.value},
        {color: '#DC2626', label: t('voip.report_cdr.not_processed_call'), value: notProcessed?.value}
      ]}
    />
  )
}

const GraphContent = ({data, dateRange}) => {
  const {t} = useTranslation()

  const customTickFormatter = tick => {
    if ([DateRange.Today, DateRange.Yesterday].includes(dateRange)) {
      return moment(tick).format('HH:mm')
    } else if ([DateRange.Week].includes(dateRange)) {
      return t(`ui_elements.days.${moment(tick).format('dddd').toLowerCase()}`)
    } else {
      return moment(tick).format('DD/MM')
    }
  }

  return (
    <div style={{ width: '100%', height: 232 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 0,
            left: 0,
            bottom: 5
          }}>
          <XAxis
            tickFormatter={customTickFormatter}
            dataKey="date"
            style={{
              fontSize: '0.75rem',
              fontWeight: 400
            }}
          />
          <YAxis
            style={{
              fontSize: '0.75rem',
              fontWeight: 400
            }}
          />
          <Tooltip
            offset={10}
            cursor={<CustomCursor />}
            wrapperStyle={{ outline: 'none', top: 0, zIndex: 10000 }}
            // @ts-expect-error 'le composant est cloné et n'a donc pas la props payload'
            content={<CustomTooltip />}
          />
          <Bar dataKey="count_call_success" fill={'#92BC62'} radius={[4, 4, 4, 4]} />
          <Bar dataKey="count_call_failure" fill={'#DC2626'} radius={[4, 4, 4, 4]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const Graph = ({dateRange, stats, loading}) => {
  const {t} = useTranslation()
  const graph = useMemo(() => {
    return stats?.graph || []
  }, [stats])

  const hasData = graph.length > 0 && graph.some(item => item.count_call_success > 0 || item.count_call_failure > 0)

  if (loading) {
    return (
      <Card
        title={t('voip.report_cdr.count_calls')}
        sx={{ borderRadius: '24px' }}
        legend={[
          <Legend key="success" color={'#92BC62'} label={t('voip.report_cdr.processed_call')} borderRadius="4px" />,
          <Legend key="failure" color={'#DC2626'} label={t('voip.report_cdr.not_processed_call')} borderRadius="4px" />
        ]}>
        <Skeleton variant={'rectangular'} width={'100%'} height={300} sx={{ borderRadius: '24px' }} />
      </Card>
    )
  }

  if (!hasData) {
    return (
      <Stack 
        sx={{
          border: '1px dashed #D7DAEA',
          borderRadius: '24px',
          minHeight: '280px',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <EmptyScreen icon={'phone-volume'} variant="compact">
          {t('voip.report_cdr.no_graph_data')}
        </EmptyScreen>
      </Stack>
    )
  }

  return (
    <Card
      title={t('voip.report_cdr.count_calls')}
      sx={{ borderRadius: '24px' }}
      legend={[
        <Legend key="success" color={'#92BC62'} label={t('voip.report_cdr.processed_call')} borderRadius="4px" />,
        <Legend key="failure" color={'#DC2626'} label={t('voip.report_cdr.not_processed_call')} borderRadius="4px" />
      ]}>
      <GraphContent data={graph} dateRange={dateRange} />
    </Card>
  )
}

export default Graph